import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSuperadminUsers, createSuperadminUser, updateSuperadminUser,
  resetSuperadminPassword, deactivateSuperadminUser,
} from '../../services/api';
import { PERMISSION_MODULES, isGodAdmin } from '../../utils/permissions';
import { toast } from 'sonner';
import {
  Shield, Plus, Pencil, KeyRound, UserX, X, Eye, EyeOff,
  Check, Loader2, Search,
} from 'lucide-react';

const PERM_KEYS = Object.keys(PERMISSION_MODULES);

const SuperadminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', full_name: '', password: '', permissions: [] });
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);

  // Edit modal
  const [showEdit, setShowEdit] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', permissions: [], is_active: true });
  const [saving, setSaving] = useState(false);

  // Reset password modal
  const [showResetPw, setShowResetPw] = useState(false);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [resetPwValue, setResetPwValue] = useState('');
  const [showResetPwValue, setShowResetPwValue] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await getSuperadminUsers();
      setUsers(res.data);
    } catch (err) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  // Only god admins see this page
  if (currentUser && !isGodAdmin(currentUser)) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64 text-slate-500">
          Solo el administrador principal puede gestionar usuarios admin.
        </div>
      </DashboardLayout>
    );
  }

  const filteredUsers = users.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return u.full_name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s);
  });

  // --- Handlers ---

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.full_name || !createForm.password) return;
    setCreating(true);
    try {
      await createSuperadminUser({
        email: createForm.email.trim().toLowerCase(),
        full_name: createForm.full_name.trim(),
        password: createForm.password,
        permissions: createForm.permissions.length > 0 ? createForm.permissions : null,
      });
      toast.success('Usuario creado exitosamente');
      setShowCreate(false);
      setCreateForm({ email: '', full_name: '', password: '', permissions: [] });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      full_name: u.full_name || '',
      permissions: u.permissions || [],
      is_active: u.is_active !== false,
    });
    setShowEdit(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    setSaving(true);
    try {
      await updateSuperadminUser(editUser.id, {
        full_name: editForm.full_name.trim(),
        permissions: editForm.permissions.length > 0 ? editForm.permissions : null,
        is_active: editForm.is_active,
      });
      toast.success('Usuario actualizado');
      setShowEdit(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPwUser || !resetPwValue) return;
    setResettingPw(true);
    try {
      await resetSuperadminPassword(resetPwUser.id, resetPwValue);
      toast.success('Contrasena reseteada');
      setShowResetPw(false);
      setResetPwValue('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al resetear');
    } finally {
      setResettingPw(false);
    }
  };

  const handleDeactivate = async (u) => {
    if (!window.confirm(`Desactivar usuario "${u.full_name}"?`)) return;
    try {
      await deactivateSuperadminUser(u.id);
      toast.success('Usuario desactivado');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al desactivar');
    }
  };

  const togglePerm = (perms, key) => {
    return perms.includes(key) ? perms.filter(p => p !== key) : [...perms, key];
  };

  // --- Permission Checkboxes Component ---
  const PermissionGrid = ({ selected, onChange }) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {PERM_KEYS.map(key => (
        <label
          key={key}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors text-sm ${
            selected.includes(key)
              ? 'border-teal-500 bg-teal-50 text-teal-800'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
          }`}
        >
          <input
            type="checkbox"
            checked={selected.includes(key)}
            onChange={() => onChange(togglePerm(selected, key))}
            className="sr-only"
          />
          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
            selected.includes(key) ? 'bg-teal-500 border-teal-500' : 'border-slate-300'
          }`}>
            {selected.includes(key) && <Check className="w-3 h-3 text-white" />}
          </div>
          <span className="truncate">{PERMISSION_MODULES[key].label}</span>
        </label>
      ))}
    </div>
  );

  const isUserGodAdmin = (u) => !u.permissions || u.permissions.length === 0;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Shield className="w-7 h-7 text-teal-600" />
              Usuarios Admin
            </h1>
            <p className="text-sm text-slate-500 mt-1">Gestiona los superadministradores del sistema</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Nuevo Admin
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Usuario</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Permisos</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Estado</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Creado</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredUsers.map(u => (
                    <tr key={u.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{u.full_name}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        {isUserGodAdmin(u) ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <Shield className="w-3 h-3" /> Admin Principal
                          </span>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {u.permissions.map(p => (
                              <span key={p} className="px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-600">
                                {PERMISSION_MODULES[p]?.label || p}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          u.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {u.is_active !== false ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('es-MX') : '-'}
                      </td>
                      <td className="px-4 py-3">
                        {!isUserGodAdmin(u) ? (
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openEdit(u)} title="Editar" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => { setResetPwUser(u); setShowResetPw(true); }} title="Reset password" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700">
                              <KeyRound className="w-4 h-4" />
                            </button>
                            {u.is_active !== false && (
                              <button onClick={() => handleDeactivate(u)} title="Desactivar" className="p-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600">
                                <UserX className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs text-slate-400 text-right">Protegido</div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-slate-400">
                        No se encontraron usuarios
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* === Create Modal === */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">Nuevo Superadmin</h2>
              <button onClick={() => setShowCreate(false)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre completo</label>
                <input
                  type="text" required
                  value={createForm.full_name}
                  onChange={e => setCreateForm(f => ({ ...f, full_name: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email" required
                  value={createForm.email}
                  onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Contrasena temporal</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'} required minLength={8}
                    value={createForm.password}
                    onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))}
                    className="w-full px-3 py-2.5 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                    placeholder="Min. 8 caracteres"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">El usuario debera cambiar la contrasena al iniciar sesion</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Permisos <span className="text-slate-400 font-normal">(dejar vacio = acceso total)</span>
                </label>
                <PermissionGrid
                  selected={createForm.permissions}
                  onChange={perms => setCreateForm(f => ({ ...f, permissions: perms }))}
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">
                  Cancelar
                </button>
                <button type="submit" disabled={creating} className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm font-medium">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Crear Usuario
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* === Edit Modal === */}
      {showEdit && editUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">Editar: {editUser.full_name}</h2>
              <button onClick={() => setShowEdit(false)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEdit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre completo</label>
                <input
                  type="text" required
                  value={editForm.full_name}
                  onChange={e => setEditForm(f => ({ ...f, full_name: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Permisos</label>
                <PermissionGrid
                  selected={editForm.permissions}
                  onChange={perms => setEditForm(f => ({ ...f, permissions: perms }))}
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))}
                    className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
                  />
                  <span className="text-sm text-slate-700">Activo</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowEdit(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">
                  Cancelar
                </button>
                <button type="submit" disabled={saving} className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm font-medium">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* === Reset Password Modal === */}
      {showResetPw && resetPwUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">Reset Password</h2>
              <button onClick={() => { setShowResetPw(false); setResetPwValue(''); }} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleResetPassword} className="p-5 space-y-4">
              <p className="text-sm text-slate-600">
                Nueva contrasena para <span className="font-medium">{resetPwUser.full_name}</span>
              </p>
              <div className="relative">
                <input
                  type={showResetPwValue ? 'text' : 'password'} required minLength={8}
                  value={resetPwValue}
                  onChange={e => setResetPwValue(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                  placeholder="Min. 8 caracteres"
                />
                <button type="button" onClick={() => setShowResetPwValue(!showResetPwValue)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showResetPwValue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => { setShowResetPw(false); setResetPwValue(''); }} className="px-4 py-2 text-sm text-slate-600">
                  Cancelar
                </button>
                <button type="submit" disabled={resettingPw || resetPwValue.length < 8} className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm font-medium">
                  {resettingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  Resetear
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SuperadminUsers;
